import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { signIn, getMe } from '@/lib/auth';
import { clearToken, getToken } from '@/lib/token';
import { HexBadge } from '@/components/HexBadge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

export const Route = createFileRoute('/login')({
  beforeLoad: () => {
    if (getToken()) {
      throw redirect({ to: '/' });
    }
  },
  component: Login,
});

const FormSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password required'),
});
type FormInput = z.infer<typeof FormSchema>;

function Login() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: zodResolver(FormSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: FormInput) => {
    setError(null);
    setBusy(true);
    try {
      await signIn(data.email, data.password);
      const me = await getMe();
      if (me.user.role !== 'admin') {
        clearToken();
        setError('Admin access only. This account is not an administrator.');
        return;
      }
      await navigate({ to: '/' }); // _authed is pathless; its index is '/'
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-cream text-ink flex items-center justify-center p-4">
      <div
        className="w-full max-w-sm bg-paper rounded-2xl p-8 flex flex-col items-center gap-6"
        style={{ boxShadow: 'var(--shadow-md)' }}
      >
        <HexBadge size={48} />
        <div className="text-center">
          <h1 className="font-serif text-3xl">Honeycomb</h1>
          <p className="text-ink-soft text-sm mt-1">Admin dashboard</p>
        </div>
        {error && (
          <div
            data-testid="login-error"
            className="w-full text-sm rounded-lg px-3 py-2"
            style={{
              background: 'var(--color-error-bg)',
              border: '1px solid var(--color-error-border)',
              color: 'var(--color-error-text)',
            }}
            role="alert"
          >
            {error}
          </div>
        )}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full flex flex-col gap-4"
          noValidate
        >
          <Input
            data-testid="login-email"
            label="Email"
            type="email"
            autoComplete="email"
            autoFocus
            {...register('email')}
            error={errors.email?.message}
          />
          <Input
            data-testid="login-password"
            label="Password"
            type="password"
            autoComplete="current-password"
            {...register('password')}
            error={errors.password?.message}
          />
          <Button data-testid="login-submit" type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </div>
    </main>
  );
}
